import {
  httpPostAssetsByAssetIdCustomKeyframeByPosterId,
  httpPostCollectionsByCollectionIdCustomKeyframeByPosterId,
} from '@iconik-io/iconik-api/src/services/files';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import ContextMenu from '../../components/context_menu/context_menu';
import contextMenuConnector from '../../components/context_menu/context_menu_connector';
import type {
  ContextMenuItemDefinition,
  GetDefinition,
} from '../../components/context_menu/types';
import {
  CONTEXT_MENU_PANELS_ENTITY_POSTERS,
  connectContextMenuConsumer,
} from '../../contexts/context_menu';
import {
  deleteKeyframe,
  downloadKeyframe,
} from '../../ducks/entity_keyframes/thunks';
import { successMessage } from '../../ducks/messages/thunks';
import { ICONS } from '../../icons/icon';
import { getCurrentRouteParams } from '../../router/current_route_params';
import type { AppDispatch } from '../../store/types';
import type { KeyframeWithId } from '../../types/files';
import { getCanUploadPosters } from './selectors';

const getDefinition: GetDefinition<KeyframeWithId> = (
  { contextItems },
  state,
) => {
  const [contextItem] = contextItems;

  const items: ContextMenuItemDefinition[] = [];

  if (!contextItem) {
    return { items };
  }

  const entity = 'asset_id' in contextItem ? 'assets' : 'collections';

  const canUploadPosters = getCanUploadPosters(
    state,
    getCurrentRouteParams().id,
    entity,
  );

  let isCurrentCustomKeyframe = false;

  const entities = state.entities;
  const collectionId = contextItem.collection_id;
  const assetId = 'asset_id' in contextItem ? contextItem.asset_id : undefined;

  if (entity === 'assets' && assetId) {
    const asset = entities.assets[assetId] ?? entities.search_items[assetId];

    if (asset?.custom_poster === contextItem.id) {
      isCurrentCustomKeyframe = true;
    }
  }

  if (entity === 'collections' && collectionId) {
    const collection =
      entities.collections[collectionId] ?? entities.search_items[collectionId];
    if (collection?.custom_poster === contextItem.id) {
      isCurrentCustomKeyframe = true;
    }
  }

  if (!isCurrentCustomKeyframe && canUploadPosters) {
    items.push({
      label: 'Set as cover image',
      icon: ICONS.Camera,
      action: () => async (dispatch: AppDispatch) => {
        const isAsset = 'asset_id' in contextItem;

        if (isAsset) {
          if (!contextItem.asset_id) {
            return;
          }
          await dispatch(
            httpPostAssetsByAssetIdCustomKeyframeByPosterId(
              contextItem.asset_id,
              contextItem.id,
            ),
          );
          dispatch(
            updateEntities({
              assets: {
                [contextItem.asset_id]: {
                  custom_poster: contextItem.id,
                },
              },
            }),
          );
        } else {
          if (!collectionId) {
            return;
          }
          await dispatch(
            httpPostCollectionsByCollectionIdCustomKeyframeByPosterId(
              collectionId,
              contextItem.id,
            ),
          );
          dispatch(
            updateEntities({
              collections: {
                [collectionId]: {
                  custom_poster: contextItem.id,
                },
              },
            }),
          );
        }

        dispatch(successMessage({ text: 'Custom keyframe scheduled' }));
      },
    });
  }

  items.push({
    label: 'Download',
    name: 'DOWNLOAD',
    icon: ICONS.Download,
    action: () => (dispatch: AppDispatch) => {
      const objectId = entity === 'assets' ? assetId : collectionId;
      if (!objectId) {
        return;
      }
      dispatch(
        downloadKeyframe({
          entity,
          entityId: objectId,
          keyframeId: contextItem.id,
        }),
      );
    },
  });

  // For assets allow to delete only non-current custom covers.
  // For collections allow to delete any custom covers.
  const allowedToDelete = entity === 'assets' ? !isCurrentCustomKeyframe : true;
  if (allowedToDelete && canUploadPosters) {
    items.push({
      label: 'Delete',
      name: 'DELETE',
      icon: ICONS.TrashBin,
      action: () => (dispatch: AppDispatch) => {
        const objectId = entity === 'assets' ? assetId : collectionId;
        if (!objectId) {
          return;
        }
        dispatch(
          deleteKeyframe({
            entity,
            entityId: objectId,
            keyframeId: contextItem.id,
          }),
        );
      },
    });
  }

  return {
    items,
  };
};

const PostersContextMenu = connectContextMenuConsumer(
  contextMenuConnector(getDefinition)(ContextMenu),
  CONTEXT_MENU_PANELS_ENTITY_POSTERS,
);

export default PostersContextMenu;
